<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useI18n } from '@/composable/useI18n'
import { useFareOptimizerStore } from '@/store/fareOptimizer'
import Dropdown from '@/components/Globals/Inputs/Dropdown.vue'

const CREATE_OPTION_ID = '__create__'

type ConsumerUnitOption = {
  id: number | typeof CREATE_OPTION_ID
  name: string
  label: string
}

const props = defineProps<{
  /** Mantém «+ Adicionar nova UC» exibido no dropdown enquanto o create está aberto. */
  creating?: boolean
}>()

const emit = defineEmits<{
  (e: 'create'): void
  (e: 'unit-selected'): void
}>()

const { t } = useI18n()
const store = useFareOptimizerStore()
const { consumerUnits, selectedConsumerUnit } = storeToRefs(store)

const createOption = computed<ConsumerUnitOption>(() => ({
  id: CREATE_OPTION_ID,
  name: t('contract_optimizer.consumer_units.add_option'),
  label: t('contract_optimizer.consumer_units.add_option'),
}))

const dropdownOptions = computed<ConsumerUnitOption[]>(() => {
  const units = consumerUnits.value.map((unit) => ({
    id: unit.id,
    name: unit.name,
    label: `${unit.name} (${unit.code})`,
  }))
  return [...units, createOption.value]
})

const alreadySelectedOption = computed<ConsumerUnitOption | undefined>(() => {
  if (props.creating) return createOption.value
  const unit = selectedConsumerUnit.value
  if (!unit) return undefined
  return {
    id: unit.id,
    name: unit.name,
    label: `${unit.name} (${unit.code})`,
  }
})

function handleOptionSelected(option: object) {
  const selected = option as ConsumerUnitOption
  if (selected.id === CREATE_OPTION_ID) {
    emit('create')
    return
  }
  emit('unit-selected')
  store.selectConsumerUnit(Number(selected.id))
}
</script>

<template>
  <div class="flex w-full flex-col gap-2 rounded-none" :aria-label="t('contract_optimizer.consumer_units.selector_aria')">
    <label class="text-sm font-medium text-slate-700">
      {{ t('contract_optimizer.consumer_units.label') }}
    </label>
    <div class="flex items-stretch gap-2">
      <div class="min-w-0 flex-1">
        <Dropdown
          :key="selectedConsumerUnit?.id ?? 'none'"
          class="rounded-none [&_input]:rounded-none [&_ul]:rounded-none [&_li]:rounded-none"
          name="consumerUnitSelector"
          label-attribute="label"
          :options="dropdownOptions"
          :already-selected-option="alreadySelectedOption"
          :placeholder="t('contract_optimizer.consumer_units.label')"
          @option-selected="handleOptionSelected" />
      </div>
      <slot name="trailing" />
    </div>
  </div>
</template>
